import { gql } from '@apollo/client';

export const deleteGroupMutation = gql`
  mutation removeGroup($groupId: Int!) {
    deleteGroup(groupId: $groupId) {
      success
      errors
    }
  }
`;

export const publishGroupToLMS = gql`
  mutation publishToLMS(
    $groupId: ID!
    $publishedLms: Boolean!
    $publishedForTeachers: Boolean
    $publishedLmsForMethodist: Boolean
  ) {
    publishGroup(
      groupId: $groupId
      publishedLms: $publishedLms
      publishedForTeachers: $publishedForTeachers
      publishedLmsForMethodist: $publishedLmsForMethodist
    ) {
      success
      errors
    }
  }
`;

export const createGroupMutation = gql`
  mutation AddGroup($groupInput: GroupInput!, $moduleInput: [ModuleInput]!) {
    createGroup(groupInput: $groupInput, moduleInput: $moduleInput) {
      success
      errors
      customErrors {
        field
        message
        code
      }
    }
  }
`;

export const UpdateGroupMutation = gql`
  mutation changeGroup($groupId: Int!, $groupInput: UpdateGroupInput!) {
    updateGroup(groupId: $groupId, groupInput: $groupInput) {
      success
      errors
      customErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Single-shot «+ Студент» from the GroupDetail HeaderBar / StudentsTab.
 * `agreementInput.groupId` carries the group the new Agreement lands in
 * (the backend mutation is group-aware via that field — see backend
 * TZ §G8). Returns the new agreement so the page can refetch and the
 * row pops into the roster.
 */
/**
 * NB: operation name is prefixed `Group…` and intentionally differs
 * from the schema payload type name (`AppendStudentToGroupMutation`)
 * so graphql-codegen doesn't emit duplicate identifiers — same pattern
 * as `mutations/zoom.ts` uses for its mutations.
 */
export const AppendStudentToGroupMutation = gql`
  mutation GroupAppendStudent(
    $studentId: ID!
    $agreementInput: CreateAgreementInputBase!
  ) {
    appendStudentToGroup(
      studentId: $studentId
      agreementInput: $agreementInput
    ) {
      success
      customErrors {
        field
        message
        code
      }
      errors
      agreement {
        id
        agreementTitle
        currency
        budgetAmount
        paidAmount
        terminated
        date
      }
    }
  }
`;

/**
 * One-click «Terminate» from the GroupDetail StudentsTab roster row
 * (Req 10.1/10.2). Invoked through `ConfirmPopup` (see `useGroupConfirm`),
 * never the generic `EditAgreementPopup`.
 *
 * B3: the backend now ships a DEDICATED `terminateAgreement` mutation, so the
 * old `updateStudentWithAgreement` echo (which re-sent the student's PII just to
 * flip a flag) is gone. Termination is now a minimal, intention-revealing call:
 * only `agreementId` + optional `terminatedDate` / `terminatedReason` / free-text
 * `comment`. The backend stores the reason inline in `comment` as a
 * `[reason=...]` tag and has no side effects on contributions, deferred payments,
 * CRM, or notifications. Repeat calls return a `customErrors` entry with code
 * `ALREADY_TERMINATED`.
 *
 * NB: operation name stays prefixed `Group…` so it differs from the schema
 * payload type name (`TerminateAgreementMutation`) and graphql-codegen doesn't
 * emit duplicate identifiers — same guard as `GroupAppendStudent` above, and it
 * keeps the generated hook name `useGroupTerminateAgreementMutation` stable.
 */
export const TerminateAgreementMutation = gql`
  mutation GroupTerminateAgreement(
    $agreementId: ID!
    $terminatedDate: Date
    $terminatedReason: TerminatedReasonEnum
    $comment: String
  ) {
    terminateAgreement(
      agreementId: $agreementId
      terminatedDate: $terminatedDate
      terminatedReason: $terminatedReason
      comment: $comment
    ) {
      success
      customErrors {
        field
        message
        code
      }
      agreement {
        id
        terminated
        terminatedDate
      }
    }
  }
`;

/**
 * «Открыть доступ ко всему курсу» — ремонт доступа студента из строки ростера.
 *
 * Доступ студента в LMS — это строки посещаемости на его занятия. Расторжение
 * сносит их за будущее, а пересчёт при последующем сохранении карточки долго
 * отстраивал только будущее: занятия, прошедшие между отключением и включением,
 * пропадали навсегда. Мутация добирает недостающие занятия договора и НИЧЕГО
 * не удаляет — деньги, взносы и `terminated` не трогает, повторное нажатие
 * возвращает `restoredLessons: 0`.
 *
 * Имя операции с префиксом `Group…` — по той же причине, что и у соседей:
 * иначе оно совпало бы с именем payload-типа схемы и codegen выдал бы
 * дублирующиеся идентификаторы.
 */
export const RestoreCourseAccessMutation = gql`
  mutation GroupRestoreCourseAccess($agreementId: ID!) {
    restoreCourseAccess(agreementId: $agreementId) {
      success
      customErrors {
        field
        message
        code
      }
      restoredLessons
      agreement {
        id
        terminated
      }
    }
  }
`;
